import AddCircleOutlined from '@mui/icons-material/AddCircleOutlined'
import Print from '@mui/icons-material/Print'
import Image from '@mui/icons-material/Image'
import OpenInNew from '@mui/icons-material/OpenInNew'
import { Chip, IconButton, Tooltip } from '@mui/material'
import { useLocation, useNavigate } from 'react-router'
import { BOOKING_STATUSES, BOOKING_PAYMENT_STATUSES, EVENT_TYPE_THERAPY_SESSION, EVENT_TYPE_SCREENING } from 'src/consumer/constants'
import { getAllRolesInBoolean, canSupportManageSessionForEventType } from 'src/mentor/layout/utils'
import PortalVoiceCallButton from 'src/mentor/components/voice/PortalVoiceCallButton'
import { EDVCSlotButton } from '../../ClinicManageBookingsPageStyles'

const ActionButtons = ({
  bookedSlot,
  forMentor,
  minimalUI,
  therapyBookingDataForThisEvent,
  handleNewTherapyRequest,
  canPersonPrintPrescription,
  showPrescriptionUploadButton,
  openReceiptDialog,
  setEventDataForReceiptPrinting,
  openUploadPrescriptionImagesDialog,
  openPaymentLinksDialog,
  openEmrImagesDialog,
  openMenu,
  onOpenMainDrawer, // New prop to open main drawer
}) => {
  const navigate = useNavigate()
  const location = useLocation()
  
  const isBookedSlotBlockedForPayment =
    bookedSlot?.status === BOOKING_STATUSES.BOOKING_INITIATED &&
    bookedSlot?.paymentStatus === BOOKING_PAYMENT_STATUSES.PENDING

  const isTherapySupport = 
    getAllRolesInBoolean().isL1TherapySupport ||
    getAllRolesInBoolean().isL2TherapySupport ||
    getAllRolesInBoolean().isL3TherapySupport

  // Check if EMR images button should be shown
  // Supports: therapy-session, screening, assessment, investigation
  const isEmrSupportedEventType = 
    bookedSlot?.eventType === EVENT_TYPE_THERAPY_SESSION || 
    bookedSlot?.eventType === EVENT_TYPE_SCREENING ||
    bookedSlot?.eventType === 'assessment' ||
    bookedSlot?.eventType === 'assessment-report' ||
    bookedSlot?.eventType === 'investigation' ||
    bookedSlot?.eventType === 'investigation-report'
  
  const isSupportPortalPath =
    location?.pathname?.startsWith('/support-home') || location?.pathname?.startsWith('/support')
  // In the support portal, only show the report-upload action to roles authorized for that session type
  // (mirrors the backend). In the practitioner (mentor) portal, the practitioner manages their own session.
  const showEmrImagesButton =
    forMentor &&
    isEmrSupportedEventType &&
    (!isSupportPortalPath || canSupportManageSessionForEventType(bookedSlot?.eventType))
  const isClinicAssistantPortalPath =
    location?.pathname?.startsWith('/ca-home') || location?.pathname?.startsWith('/kiosk')
  
  // Get button label based on event type
  const getEmrButtonLabel = () => {
    const eventType = bookedSlot?.eventType
    if (eventType === EVENT_TYPE_THERAPY_SESSION) return 'Upload Therapy Report Images'
    if (eventType === EVENT_TYPE_SCREENING) return 'Upload Screening Report Images'
    if (eventType === 'assessment' || eventType === 'assessment-report') return 'Upload Assessment Report Images'
    if (eventType === 'investigation' || eventType === 'investigation-report') return 'Upload Investigation Report Images'
    return 'Upload EMR Images'
  }

  return (
    <div
      style={{
        display: 'flex',
        gap: '3px',
        alignItems: 'center',
        flexShrink: 0, // Never shrink action buttons
        minWidth: 'fit-content', // Ensure plus button always fits
      }}
    >
      {/* Book Therapist Button */}
      {!forMentor && therapyBookingDataForThisEvent && (
        <EDVCSlotButton
          style={{ background: '#fcff02' }}
          onClick={(event) => {
            event.stopPropagation()
            handleNewTherapyRequest(therapyBookingDataForThisEvent, bookedSlot)
          }}
        >
          Book Therapist
        </EDVCSlotButton>
      )}

      {/* Print Prescription Button */}
      {canPersonPrintPrescription && bookedSlot?.markedCompleted && (
        <EDVCSlotButton
          onClick={() => {
            navigate(`prescription/${bookedSlot?.id}`)
          }}
        >
          <Print sx={{ fontSize: '20px' }} />
        </EDVCSlotButton>
      )}

      {/* Recording Indicator */}
      {isTherapySupport && bookedSlot?.recordingEnabled && (
        <Chip color="error" label="Rec" />
      )}

      {/* Invoice Button */}
      {!forMentor && (
        <EDVCSlotButton
          style={{
            fontSize: '13px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '2px',
          }}
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            openReceiptDialog()
            setEventDataForReceiptPrinting(bookedSlot)
          }}
        >
          <span>Invoice</span>
        </EDVCSlotButton>
      )}

      {isClinicAssistantPortalPath && bookedSlot?.id && (
        <PortalVoiceCallButton
          actorRole="clinic-assistant"
          bookedEventId={bookedSlot.id}
          uiSurface="clinic-assistant-day-calendar-slot"
          renderTrigger={({ onClick, label, busy, active, disabled }) => (
            <EDVCSlotButton
              style={{
                fontSize: '13px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '2px',
                background: active ? '#fee2e2' : '#dbeafe',
                color: active ? '#991b1b' : '#1d4ed8',
                border: `1px solid ${active ? '#fca5a5' : '#93c5fd'}`,
              }}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                onClick?.(e)
              }}
              disabled={disabled}
            >
              <span>{busy ? 'Calling...' : label}</span>
            </EDVCSlotButton>
          )}
        />
      )}

      {/* Prescription Upload Button */}
      {showPrescriptionUploadButton && (
        <EDVCSlotButton
          style={{
            fontSize: '13px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '2px',
          }}
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            openUploadPrescriptionImagesDialog()
          }}
        >
          <span>Rx</span>
        </EDVCSlotButton>
      )}

      {/* EMR Images Button */}
      {showEmrImagesButton && (
        <Tooltip title={getEmrButtonLabel()} arrow>
          <EDVCSlotButton
            style={{
              fontSize: '13px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '2px',
            }}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              openEmrImagesDialog()
            }}
          >
            <Image sx={{ fontSize: '16px' }} />
          </EDVCSlotButton>
        </Tooltip>
      )}

      {/* Payment Links Button */}
      {forMentor && isBookedSlotBlockedForPayment && (
        <EDVCSlotButton
          style={{
            fontSize: '13px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '2px',
          }}
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            openPaymentLinksDialog()
          }}
        >
          <span>Payment Links</span>
        </EDVCSlotButton>
      )}

      {/* Open Main Drawer Button - for Mentor Portal */}
      {forMentor && onOpenMainDrawer && (
        <Tooltip title="View Full Details">
          <IconButton
            size="small"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              if (onOpenMainDrawer && bookedSlot) {
                onOpenMainDrawer(bookedSlot)
              }
            }}
            sx={{
              padding: '4px',
              color: 'rgba(67, 56, 202, 0.7)',
              '&:hover': {
                backgroundColor: 'rgba(67, 56, 202, 0.1)',
                color: 'rgba(67, 56, 202, 0.9)',
              },
            }}
          >
            <OpenInNew sx={{ fontSize: '18px' }} />
          </IconButton>
        </Tooltip>
      )}

      {/* More Options Menu */}
      {!minimalUI && (
        <AddCircleOutlined onClick={openMenu} />
      )}
    </div>
  )
}

export default ActionButtons
